<template>
  <table class="table table-striped border-light">
    <thead>
    <tr>
      <th colspan="2">
        <h5 class="mb-0 text-center text-capitalize-first">
          {{ helper.trans('labels.invoice') }}
        </h5>
      </th>
    </tr>
    </thead>
    <tbody>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.id') }}</th>
      <td>{{ invoice.id }} <copy-button :value="invoice.id"></copy-button></td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.type') }}</th>
      <td>{{ helper.trans(`direction.${invoice.type}`) }}</td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.date') }}</th>
      <td>{{ dateHelper.format(invoice.createdAt) }}</td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.uid') }}</th>
      <td>{{ invoice.userId }}</td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.sum') }}</th>
      <td>
            <span>
              {{ numberFormatter.formatMoney(invoice.sum) }}
              <copy-button :value="numberFormatter.formatNumber(invoice.sum)"></copy-button>
          </span>
      </td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.status') }}</th>
      <td>{{ helper.trans(`invoice_status.${invoice.status}`) }}</td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.paid_sum') }}</th>
      <td v-if="invoice.receivedFromMaker && invoice.receivedFromTaker">
        {{ numberFormatter.formatMoney(invoice.receivedFromTaker) }} / {{ numberFormatter.formatMoney(invoice.receivedFromMaker) }}
        <small v-if="invoice.rate" class="ms-1">({{helper.trans('labels.rate')}} {{invoice.rate}})</small>
      </td>
      <td v-else>n/a</td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.service_fee') }}</th>
      <td v-if="!invoice.serviceFee">n/a</td>
      <td v-else><span class="text-danger">{{numberFormatter.formatMoney(invoice.serviceFee)}}</span></td>
    </tr>
    <tr>
      <th class="text-capitalize-first">{{ helper.trans('labels.payout_sum') }}</th>
      <td v-if="!invoice.payoutSum">n/a</td>
      <td v-else>
        <span :class="{'text-success': invoice.type === Direction.PAY_IN, 'text-danger': invoice.type === Direction.PAY_OUT}">
          {{invoice.type === Direction.PAY_IN ? '+' : '-'}}{{numberFormatter.formatMoney(invoice.payoutSum)}}
        </span>
      </td>
    </tr>
    <tr v-if="invoice?.type === Direction.PAY_IN">
      <th class="text-capitalize-first">{{ helper.trans('labels.url') }}</th>
      <td>
        <a :href="invoice.payUrl" target="_blank">{{ invoice.payUrl }}</a>
      </td>
    </tr>
    <tr v-if="isAdmin">
      <td colspan="2" class="text-end pt-3">
        <button
          type="button"
          @click="showRequestLog"
          class="btn btn-outline-secondary btn-sm text-capitalize-first"
        >
          {{ helper.trans('labels.show_request_log') }}
        </button>

        <button 
          type="button" 
          @click="showWebhooksHistory" 
          class="btn btn-outline-secondary btn-sm text-capitalize-first ms-1"
        >
          {{ helper.trans('labels.show_webhooks_history') }}
        </button>

        <button
          type="button"
          @click="showRequestsTimeline"
          class="btn btn-outline-secondary btn-sm text-capitalize-first ms-1"
        >
          {{ helper.trans('labels.show_requests_timeline') }}
        </button>
      </td>
    </tr>
    </tbody>
  </table>
  <InvoiceRequestsTimelineModal ref="invoiceRequestsTimelineModal"></InvoiceRequestsTimelineModal>
  <WebhookHistoryModal ref="webhookHistoryModal"></WebhookHistoryModal>
  <MerchantRequestLogModal ref="merchantRequestLogModal"></MerchantRequestLogModal>
</template>

<script lang="ts">

import {computed, defineComponent, Prop, PropType, reactive, ref, toRefs} from 'vue'
import {helper} from "@/service/CommonHelper";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/components/CopyButton.vue";
import InvoiceDTO from "@/models/InvoiceDTO";
import {DealStatus} from "@/models/enum/DealStatus";
import {Direction} from "@/models/enum/Direction";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {BModal} from "bootstrap-vue-3";
import {useStore} from "vuex";
import InvoiceRequestsTimelineModal from "@/components/InvoiceRequestsTimelineModal.vue";
import WebhookHistoryModal from "@/components/WebhookHistoryModal.vue";
import MerchantRequestLogModal from "@/components/MerchantRequestLogModal.vue";

export default defineComponent({
  components: {
    MerchantRequestLogModal,
    WebhookHistoryModal,
    InvoiceRequestsTimelineModal,
    CopyButton,
    LoaderWrapper,
    BModal
  },
  props: {
    invoice: {
      type: Object as PropType<InvoiceDTO>,
      default: null
    } as Prop<InvoiceDTO>
  },
  setup(props) {
    const store = useStore();
    const merchantRequestLogModal = ref<InstanceType<typeof MerchantRequestLogModal> | null>(null)
    const webhookHistoryModal = ref<InstanceType<typeof WebhookHistoryModal> | null>(null)
    const webhooksModalLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const invoiceRequestsTimelineModal = ref<InstanceType<typeof InvoiceRequestsTimelineModal> | null>(null);

    const state = reactive({
    });


    const showRequestsTimeline = async () => {
      if (!props.invoice) return;

      invoiceRequestsTimelineModal.value?.show(props.invoice.id)
    }

    const showWebhooksHistory = async () => {
      webhookHistoryModal.value?.show(props.invoice?.id!)
    }

    const showRequestLog = async () => {
      merchantRequestLogModal.value?.show(props.invoice?.id!)
    }

    return {
      ...toRefs(state),
      dateHelper,
      numberFormatter,
      DealStatus,
      helper,
      Direction,
      webhooksModalLoader,
      invoiceRequestsTimelineModal,
      showWebhooksHistory,
      showRequestLog,
      merchantRequestLogModal,
      showRequestsTimeline,
      webhookHistoryModal,
      isAdmin: computed(() => store.getters.isAdmin),
    }
  }
})
</script>
